// Sales performance (step 10.8): the page's types, the call to the "performance" Edge Function (the ask bar), and
// the pure helpers the page uses. Every figure comes from the database (crm_performance, crm_metric_query); the
// model behind the ask bar only picks a metric, a breakdown and dates (docs/SECURITY.md, "Sales performance").
import { env, functionUrl } from "./env";
import { httpFetch } from "./http";
import { formatValue, summarise } from "./performanceSummary";

export type Unit = "count" | "percent" | "money";
export type By = "none" | "pool" | "module" | "route" | "audience" | "week" | "month" | "channel";
export type PoolKey = "inbound" | "outbound" | "network" | "invited";

export interface MetricSpec {
  metric: string;
  by?: By;
  from?: string;
  to?: string;
  pools?: PoolKey[];
}
export interface MetricRow {
  key: string | null;
  label: string;
  value: number | null;
  numerator?: number | null;
  denominator?: number | null;
}
export interface MetricResult {
  metric: string;
  label: string;
  unit: Unit;
  means?: string;
  currency: string;
  by: By | string;
  from: string;
  to: string;
  pools: string[];
  total: number | null;
  rows: MetricRow[];
}

export interface Headline {
  key: "leads" | "meetings" | "won" | "revenue_won";
  label: string;
  unit: Unit;
  value: number;
  previous: number;
  target: number | null;
}
export interface Stage {
  key: string;
  label: string;
  count: number;
}
export interface PoolFunnel {
  key: PoolKey;
  label: string;
  leads: number;
  contacted: number;
  replied: number;
  meeting: number;
  proposal: number;
  won: number;
  revenue: number;
}
export interface Velocity {
  key: string;
  label: string;
  median_days: number | null;
  leads: number;
  target_days: number | null;
}
export interface QuietDeal {
  deal_id: string;
  organisation_id: string;
  name: string;
  stage: string;
  stage_label: string;
  value: number | null;
  days_quiet: number;
  last_activity_at: string;
  nudge_waiting: boolean;
}
export interface AutomationRow {
  key: string;
  label: string;
  count: number;
  minutes_each: number | null;
}
export interface Note {
  key: string;
  kind: "watch" | "waiting" | "opportunity" | "good";
  headline: string;
  detail: string;
  link: { label: string; to: string; ask?: string } | null;
}
export interface Pin {
  id: string;
  title: string;
  question: string | null;
  spec: MetricSpec;
  created_by: string;
  created_at: string;
}
/** By module (migration 20261106090000): firms in each module now, moves in and out in the period, and time spent. */
export interface ModuleRow {
  key: string;
  label: string;
  coming_soon: boolean;
  now: number;
  entered: number;
  left: number;
  stays_ended: number;
  median_days: number | null;
  median_days_so_far: number | null;
}
/** Leads that arrived in the period, by the route or audience of the first CTA they used ("none": not from content). */
export interface SourceRow {
  key: string;
  label: string;
  leads: number;
  meetings: number;
  won: number;
  revenue: number;
}
export interface Performance {
  days: number;
  from: string;
  to: string;
  currency: string;
  targets_set: boolean;
  headline: Headline[];
  stages: Stage[];
  pools: PoolFunnel[];
  velocity: Velocity[];
  quiet: QuietDeal[];
  automation: { rows: AutomationRow[]; meetings_total: number; meetings_by_agent: number; hours_saved: number };
  notes: Note[];
  pins: Pin[];
  /** Added by migration 20261106090000; older databases leave them out. */
  modules?: ModuleRow[];
  routes?: SourceRow[];
  audiences?: SourceRow[];
}

export interface PerformanceAnswer {
  spec: MetricSpec;
  title: string;
  result: MetricResult;
  summary: string;
}

/** The periods the page offers. */
export const PERIODS: { days: number; label: string }[] = [
  { days: 30, label: "Last 30 days" },
  { days: 90, label: "Last 90 days" },
  { days: 365, label: "Last 12 months" },
];

/** Questions to try, shown under the ask bar. */
export const EXAMPLE_QUESTIONS = [
  "Reply rate by week",
  "Which lead pool brought the most revenue?",
  "Meetings booked by month this year",
  "Leads by route",
  "Moves into each module by week",
];

export const MAX_PINS = 12;
export const MAX_QUESTION = 500;

export class PerformanceError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = "PerformanceError";
  }
}

/** The ask bar: POST {question, previous_spec?} to the performance Edge Function. */
export async function askPerformance(opts: {
  token: string;
  question: string;
  previousSpec?: MetricSpec | null;
  url?: string;
  anonKey?: string;
  fetchImpl?: typeof fetch;
}): Promise<PerformanceAnswer> {
  const f = opts.fetchImpl ?? httpFetch;
  let res: Response;
  try {
    res = await f(opts.url ?? functionUrl("performance"), {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${opts.token}`, apikey: opts.anonKey ?? env.supabaseAnonKey },
      body: JSON.stringify({ question: opts.question, ...(opts.previousSpec ? { previous_spec: opts.previousSpec } : {}) }),
    });
  } catch {
    throw new PerformanceError("Can't reach the server. Check your connection and try again.", 0);
  }
  const body = (await res.json().catch(() => ({}))) as Partial<PerformanceAnswer> & { error?: string };
  if (res.status === 404 && !body.error) {
    throw new PerformanceError("Asking about your figures isn't switched on yet. Try again later, or ask us for help.", 404);
  }
  if (res.status === 401) throw new PerformanceError("Your session has ended. Sign in again.", 401);
  if (!res.ok || !body.spec || !body.result) throw new PerformanceError(body.error ?? "Something went wrong. Try again in a moment.", res.status);
  return {
    spec: body.spec,
    title: body.title || body.result.label,
    result: body.result,
    summary: body.summary ?? summarise(body.result),
  };
}

/** A pinned spec with no dates of its own follows the page's period. */
export function specForPeriod(spec: MetricSpec, days: number, today = new Date()): MetricSpec {
  if (spec.from || spec.to) return spec;
  const to = today.toISOString().slice(0, 10);
  const from = new Date(today.getTime() - (days - 1) * 86_400_000).toISOString().slice(0, 10);
  return { ...spec, from, to };
}

/** "+9 on the prior 30 days", "Same as the prior 30 days", "−3 on …". A minus sign, never red. */
export function deltaText(h: Pick<Headline, "value" | "previous" | "unit">, days: number, currency: string): string {
  const d = h.value - h.previous;
  const span = `the prior ${days} days`;
  if (d === 0) return `Same as ${span}`;
  const abs = h.unit === "money" ? formatValue(Math.abs(d), "money", currency) : Math.abs(d).toLocaleString("en-GB");
  return `${d > 0 ? "+" : "−"}${abs} on ${span}`;
}

/** Share of target, 0 to 100+ (whole numbers), or null with no target. */
export function percentOfTarget(h: Pick<Headline, "value" | "target">): number | null {
  if (h.target === null || h.target === undefined || h.target <= 0) return null;
  return Math.round((100 * h.value) / h.target);
}

/** Ten notches: how many are inked for a share of target (capped at ten). */
export function notchesFor(pct: number | null, total = 10): boolean[] {
  const on = pct === null ? 0 : Math.max(0, Math.min(total, Math.round((pct / 100) * total)));
  return Array.from({ length: total }, (_, i) => i < on);
}

export const FUNNEL_STEPS: { key: keyof PoolFunnel; label: string }[] = [
  { key: "leads", label: "Lead" },
  { key: "contacted", label: "Contacted" },
  { key: "replied", label: "Replied" },
  { key: "meeting", label: "Meeting" },
  { key: "proposal", label: "Proposal" },
  { key: "won", label: "Won" },
];

/** The step where a pool loses the largest share of its leads (with at least `min` going in), or null. */
export function biggestDrop(p: PoolFunnel, min = 1): { from: string; to: string; rate: number } | null {
  let worst: { from: string; to: string; rate: number } | null = null;
  for (let i = 1; i < FUNNEL_STEPS.length; i++) {
    const a = Number(p[FUNNEL_STEPS[i - 1]!.key] ?? 0);
    const b = Number(p[FUNNEL_STEPS[i]!.key] ?? 0);
    if (a < min) break;
    const rate = b / a;
    if (!worst || rate < worst.rate) worst = { from: FUNNEL_STEPS[i - 1]!.label, to: FUNNEL_STEPS[i]!.label, rate };
  }
  return worst;
}

/** "41% of replied": the share of the step before that reached this one. */
export function stageConversion(stages: Stage[], i: number): string {
  if (i === 0) return stages.length ? "in the period" : "";
  const prev = stages[i - 1]!.count;
  if (!prev) return "—";
  return `${Math.round((100 * stages[i]!.count) / prev)}% of ${stages[i - 1]!.label.toLowerCase()}`;
}

export function rate(n: number, d: number): string {
  if (!d) return "—";
  return `${((100 * n) / d).toFixed(1)}%`;
}

/** Inked notches for a deal at a stage, out of six funnel steps (a proposal is five of six). */
export function dealNotches(stage: string): boolean[] {
  const done = stage === "won" ? 6 : /negotiat/.test(stage) ? 5 : /proposal|offer|nda/.test(stage) ? 5 : 4;
  return Array.from({ length: 6 }, (_, i) => i < done);
}

export { formatValue, summarise };

/** "12 days", "1 day", "under a day", or "—". */
export function daysText(d: number | null | undefined): string {
  if (d === null || d === undefined || Number.isNaN(d)) return "—";
  if (d < 1) return "under a day";
  const n = Math.round(d);
  return n === 1 ? "1 day" : `${n.toLocaleString("en-GB")} days`;
}

/** The modules to show in "By module": every working module, and Complete or Closed only when someone is there. */
export function shownModules(rows: ModuleRow[]): ModuleRow[] {
  return rows.filter((m) => !(m.key === "closed" || m.coming_soon) || m.now > 0 || m.entered > 0);
}
